import './NavIcon.css';

export type IconName =
  | 'today'
  | 'sessions'
  | 'drills'
  | 'tactics'
  | 'calendar'
  | 'players'
  | 'reviews'
  | 'settings'
  | 'brain'
  | 'mic'
  | 'arrow'
  | 'plus'
  | 'check';

interface NavIconProps {
  name: IconName;
  size?: number;
  className?: string;
}

export function NavIcon({ name, size = 16, className = '' }: NavIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`nav-icon ${className}`.trim()}
      aria-hidden="true"
    >
      {pathFor(name)}
    </svg>
  );
}

function pathFor(name: IconName) {
  switch (name) {
    case 'today':
      return <path d="M2 7l6-5 6 5v7H2z" />;
    case 'sessions':
      return (
        <>
          <circle cx="8" cy="8" r="6" />
          <path d="M2 8h12M8 2v12" strokeOpacity="0.6" strokeWidth="1" />
        </>
      );
    case 'drills':
      // Cones + ball motif — a triangle of training cones with a ball
      // tracing a line through them. Reads as "drill exercise" at small sizes.
      return (
        <>
          <path d="M3 13l1.5-3 1.5 3zM8 13l1.5-3 1.5 3zM13 13l-1.5-3-1.5 3" strokeWidth="1.2" />
          <circle cx="8" cy="4" r="2" />
          <path d="M6 5.5L4.5 10M10 5.5L11.5 10" strokeWidth="0.8" strokeDasharray="1.5 1.5" />
        </>
      );
    case 'tactics':
      // Mini formation: a back-line + a forward triangle — reads as
      // "tactical formation" at small sizes.
      return (
        <>
          <circle cx="3" cy="11" r="1.2" />
          <circle cx="8" cy="11" r="1.2" />
          <circle cx="13" cy="11" r="1.2" />
          <circle cx="5.5" cy="6" r="1.2" />
          <circle cx="10.5" cy="6" r="1.2" />
          <circle cx="8" cy="2.5" r="1.2" />
        </>
      );
    case 'calendar':
      return (
        <>
          <rect x="2" y="3" width="12" height="11" rx="1.5" />
          <path d="M2 6h12M5 2v3M11 2v3" />
        </>
      );
    case 'players':
      return (
        <>
          <circle cx="6" cy="6" r="2.4" />
          <circle cx="11.5" cy="7" r="1.8" />
          <path d="M2 13c0-2 2-3 4-3s4 1 4 3M9 13c.4-1.4 1.6-2.2 2.8-2.2 1.2 0 2.4.8 2.8 2.2" />
        </>
      );
    case 'reviews':
      return (
        <>
          <path d="M3 3h7l3 3v7H3z" />
          <path d="M10 3v3h3" />
        </>
      );
    case 'settings':
      return (
        <>
          <circle cx="8" cy="8" r="2" />
          <path d="M8 1v2M8 13v2M1 8h2M13 8h2M3 3l1.4 1.4M11.6 11.6L13 13M3 13l1.4-1.4M11.6 4.4L13 3" />
        </>
      );
    case 'brain':
      return (
        <path d="M5 2.5a2.5 2.5 0 0 0 0 5v.5a2 2 0 0 0 4 0V2.5a2.5 2.5 0 0 0-4 0zM11 4.5a2.5 2.5 0 0 1 0 5v.5a2 2 0 0 1-4 0" />
      );
    case 'mic':
      return (
        <>
          <rect x="6" y="2" width="4" height="8" rx="2" fill="currentColor" stroke="none" />
          <path d="M3 8a5 5 0 0 0 10 0M8 13v2" />
        </>
      );
    case 'arrow':
      return <path d="M3 8h10M9 4l4 4-4 4" strokeWidth="1.6" />;
    case 'plus':
      return <path d="M8 3v10M3 8h10" strokeWidth="1.6" />;
    case 'check':
      return <path d="M3 8l3 3 7-7" strokeWidth="1.8" />;
  }
}
